import { c as _c } from "react/compiler-runtime";
import { LAYOUT_CONTAINER_ID } from '@onefootprint/idv';
import { useFootprintProvider } from '@onefootprint/idv2';
import { useEffect, useLayoutEffect, useState } from 'react';
const hasInlineAutoResizeFeature = (features: unknown): boolean => {
  return Array.isArray(features) && features.includes('inline-auto-resize');
};
const observeContainer = (container: HTMLElement, emitResized: (payload: {
  height: number | null;
}) => void): (() => void) => {
  let lastHeight = 0;
  let isSuspended = container.hasAttribute('data-full-bleed');
  const emitHeight = (measured: number) => {
    const height = Math.ceil(measured);
    if (isSuspended || height <= 0 || height === lastHeight) {
      return;
    }
    lastHeight = height;
    emitResized({
      height
    });
  };
  const resizeObserver = new ResizeObserver(entries => {
    const entry = entries[entries.length - 1];
    // Prefer the observed border-box size: bounding rects are skewed by
    // framer-motion transforms mid-transition.
    const boxSize = entry?.borderBoxSize?.[0]?.blockSize;
    emitHeight(typeof boxSize === 'number' ? boxSize : container.offsetHeight);
  });
  const fullBleedObserver = new MutationObserver(() => {
    const isFullBleed = container.hasAttribute('data-full-bleed');
    if (isFullBleed === isSuspended) {
      return;
    }
    isSuspended = isFullBleed;
    if (isFullBleed) {
      lastHeight = 0;
      emitResized({
        height: null
      });
    } else {
      emitHeight(container.offsetHeight);
    }
  });
  resizeObserver.observe(container);
  fullBleedObserver.observe(container, {
    attributes: true,
    attributeFilter: ['data-full-bleed']
  });
  if (isSuspended) {
    emitResized({
      height: null
    });
  } else {
    emitHeight(container.offsetHeight);
  }
  return () => {
    resizeObserver.disconnect();
    fullBleedObserver.disconnect();
  };
};
const useInlineAutoResize = isInline => {
  const $ = _c(8);
  const fpProvider = useFootprintProvider();
  const [isEnabled, setIsEnabled] = useState(false);
  let t0;
  let t1;
  if ($[0] !== fpProvider || $[1] !== isInline) {
    t0 = () => {
      if (!isInline) {
        return;
      }
      let isCancelled = false;
      const detectSupport = async () => {
        await fpProvider.load();
        if (isCancelled) {
          return;
        }
        const features = fpProvider.getAdapterResponse?.()?.model?.sdkFeatures;
        if (hasInlineAutoResizeFeature(features)) {
          setIsEnabled(true);
        }
      };
      detectSupport();
      return () => {
        isCancelled = true;
      };
    };
    t1 = [isInline, fpProvider];
    $[0] = fpProvider;
    $[1] = isInline;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== fpProvider || $[5] !== isEnabled) {
    t2 = () => {
      if (!isEnabled) {
        return;
      }
      let isCancelled_0 = false;
      let rafId = 0;
      let teardown;
      const setUp = () => {
        if (isCancelled_0) {
          return;
        }
        const container = document.getElementById(LAYOUT_CONTAINER_ID);
        if (!container) {
          rafId = requestAnimationFrame(setUp);
          return;
        }
        teardown = observeContainer(container, fpProvider.resized);
      };
      setUp();
      return () => {
        isCancelled_0 = true;
        cancelAnimationFrame(rafId);
        teardown?.();
      };
    };
    t3 = [isEnabled, fpProvider];
    $[4] = fpProvider;
    $[5] = isEnabled;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useLayoutEffect(t2, t3);
  return isEnabled;
};
export { hasInlineAutoResizeFeature };
export default useInlineAutoResize;